<template>
  <div class="c-form__group">
    <form-label :required="required" v-if="label" :for="name">{{label}}</form-label>
    <p v-if="helperText" class="c-form__helper-text">{{helperText}}</p>

    <slot>
      <div class="c-form__input-wrapper" :class="{'is-inline':isInline}">
        <textarea
          v-if="inputType == 'textarea'"
          :class="inputClassList"
          :name="name"
          :id="name"
          rows="8"
          v-model="compValue"
          autocomplete="off"
          :maxlength="maxLength"
          :placeholder="placeholder"
        ></textarea>

        <input
          v-else
          :class="inputClassList"
          :style="styleObject"
          :id="name"
          :name="name"
          :type="inputType"
          v-model="compValue"
          autocomplete="off"
          :maxlength="maxLength"
          :placeholder="placeholder"
          @focus="inputFocus = true"
          @blur="inputFocus = false"
          :pattern="pattern"
          ref="inputField"
        />
        <input-counter v-if="showCounter && maxLength" :value="inputValue" :max="maxLength"></input-counter>
        <base-button
          v-if="withButton"
          :primary="true"
          @click="$emit('click')"
          type="button"
          class="c-button--group"
        >{{buttonText}}</base-button>

        <span
          v-if="dynamicWidth"
          class="c-form__input-span"
          ref="testing1"
          :class="inputClassList"
        >{{inputValue}}</span>

        <button
          @click="focusOnInput"
          v-show="!inputFocus"
          class="c-form__input-edit"
          v-if="inputValue && dynamicWidth && ghost"
          aria-labelledby="editLabel"
        >
          <span id='editLabel'>Edit</span>
        </button>
      </div>
    </slot>
    <span class="c-form__validation-message" v-if="error">{{ error }}</span>
  </div>
</template>

<script>
import inputCounter from "./InputCounter";

export default {
  name: "form-field",

  props: {
    label: String,
    helperText: String,
    name: String,
    type: String,
    placeholder: String,
    value: [String, Number, Object, Array, Boolean],
    error: String,
    showCounter: Boolean,
    maxLength: [Number, Boolean],
    fullWidth: Boolean,
    dynamicWidth: Boolean,
    minWidth: String,
    ghost: Boolean,
    large: Boolean,
    required: [Boolean, String],
    withButton: Boolean,
    isInline: Boolean,
    buttonText: {
      type: String,
      default: ""
    },
    pattern: {
      type: String,
      default: ""
    }
  },

  data() {
    return {
      inputValue: this.value,
      spanWidth: null,
      inputFocus: false
    };
  },

  mounted() {
    if (this.dynamicWidth) this.spanWidth = this.getSpanWidth();
  },

  updated() {
    if (this.dynamicWidth) this.spanWidth = this.getSpanWidth();
  },

  methods: {
    focusOnInput() {
      this.$refs.inputField.focus();
      this.inputFocus = true;
    },
    getSpanWidth() {
      return this.$refs.testing1
        ? this.$refs.testing1.getBoundingClientRect().width + 10
        : 10;
    }
  },

  computed: {
    inputType() {
      if (!this.type) {
        return "text";
      }
      return this.type;
    },

    compValue: {
      get() {
        return this.value;
      },
      set(newVal) {
        this.inputValue = newVal;
      }
    },

    styleObject() {
      return {
        width: this.dynamicWidth ? `${this.spanWidth}px` : false,
        minWidth: !this.inputValue && this.minWidth ? this.minWidth : "auto"
      };

      return false;
    },

    inputClassList() {
      return {
        "c-form__input": true,
        "c-form__input--ghost": this.ghost,
        "c-form__input--large": this.large,
        "c-form__input--full-width": this.fullWidth,
        "c-form__input--dynamic-width": this.dynamicWidth,
        "c-form__input--with-button": this.withButton,
        "has-errors": this.error
      };
    }
  },

  watch: {
    inputValue(value, oldValue) {
      const vm = this;
      this.$emit("input", value);
    }
  },

  components: {
    inputCounter
  }
};
</script>

<style lang="scss">
.c-form__group {
  margin-bottom: 2em;
  position: relative;
}

.c-form__helper-text {
  font-size: 14px;
  margin: 0;
  margin-bottom: 1em;
}

.c-form__input-wrapper {
  position: relative;
  display: flex;

  &.is-inline {
    display: inline-block;
  }
}

.c-form__input {
  border: 2px solid #c9ced4;
  border-radius: 4px;
  background: $colour-nhs-white;
  font-size: 16px;
  padding: 1em;
  display: block;
  width: auto;
  padding-right: 3em;
  max-width: 100%;

  &.has-errors {
    border-color: $colour-nhs-emergency-red;
  }
}
textarea.c-form__input {
  width: 100%;
}
select.c-form__input {
  height: 54px;
}

.c-form__input:focus {
  border: 2px solid $colour-nhs-blue;
  outline: none;
}

.c-form__validation-message {
  display: inline-block;
  margin-top: 0.5em;
  color: $colour-nhs-emergency-red;
}

.c-form__input--ghost {
  border: none;
  padding: 0.5em 0;
}
.c-form__input--ghost:focus {
  border: none;
}
.c-form__input--large {
  font-size: 24px;
  font-family: $font-frutiger-light;
}
.c-form__input--full-width {
  display: block;
  width: 100%;
}

.c-form__input--with-button {
  display: inline-block;
  width: 100%;
  @media screen and (min-width: 768px) {
    width: 70%;
  }
}
.c-button--group {
  width: 100%;
  @media screen and (min-width: 768px) {
    width: 30%;
  }
  border-radius: 0;
}
.c-form__input--dynamic-width {
  padding-right: 42px;
  display: inline-block;
}
.c-form__input-span {
  display: inline-block;
  visibility: hidden;
  opacity: 0;
  position: absolute;
  padding-right: 42px;

  top: 0;
  left: 0;
  z-index: -1;
}
.c-form__input-edit {
  font-size: 14px;
  color: $colour-nhs-blue;
  font-weight: 600;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  appearance: none;
  border: none;
  display: inline-block;
  max-width: 100%;
  cursor: pointer;
  background: transparent;
}
</style>
